import { formatInternationalPhoneNumber } from "@brexhq/component-library";
import { optional } from "decoders";
import { useAtomValue } from "jotai";
import React from "react";
import type { UseFormGetValues } from "react-hook-form";
import { v4 as uuidv4 } from "uuid";

import {
  createLeadMutation as marketoCreateLeadMutation,
  updateLeadMutation as marketoUpdateLeadMutation,
} from "@brexhq/next/src/api/marketo";
import {
  LeadRoutingEventType,
  ScriptLoadingStatus,
  ProductInterestOptionValue,
  decodeProductInterestSelection,
  FundingSourceOptionValue,
  decodeFundingSourceOptionValue,
  EmployeeCountOptionValue,
  decodeEmployeeCountOptionValue,
  CreditSpendOptionValue,
  decodeCreditSpendOptionValue,
  EMPOWER_MULTI_STEP_LEAD_FORM,
  FORM_ID,
  Step,
  FormMetaProps,
} from "@brexhq/next/src/components/modules/FormMultistep/types";
import useAnalytics from "@brexhq/next/src/hooks/useAnalytics";
import useSiteContext from "@brexhq/next/src/hooks/useSiteContext";
import { referrerAtom } from "@brexhq/next/src/state/prevPath";
import {
  FormStepViewedEvent,
  LeadRoutingEvent,
  MarketingFormSubmittedEvent,
} from "@brexhq/next/src/telemetry/analyticsEvents";
import {
  getFBC,
  getFBP,
  getGCLID,
  getLiFatId,
  getRefCode,
  getUserRefCode,
} from "@brexhq/next/src/telemetry/attributionMetaData";
import { getAnonymousId } from "@brexhq/next/src/telemetry/initAnalytics";
import {
  logMiscError,
  logThirdPartyScriptError,
} from "@brexhq/next/src/telemetry/logging";
import { getCookieCategories } from "@brexhq/next/src/utils/analytics";
import updateCookie from "@brexhq/next/src/utils/updateCookies";
import {
  type CreateLeadInput,
  type UpdateLeadInput,
  LeadFormStatus,
  LeadFormType,
  QuestionCreditCardSpendConsolidated,
  QuestionEmployeeCount,
  QuestionFundingSource,
  QuestionIntent,
  LeadDetailsInput,
} from "@brexhq/next/types/brex-api";
import { WindowTypes } from "@brexhq/next/types/window";

import { AnalyticsApi } from "../../../telemetry/types";
import { localStorage } from "../../../utils/localStorage";
import { FormValues } from "../../building-blocks/MarketoForm/types";

import { withErrorHandling } from "./helpers";
import { useLeadFormFields, useLeadRoutingScript } from "./hooks";
import {
  mapIsEcommToMarketo,
  mapMonthlySalesToMarketo,
  mapSalesVolume,
  mapSelfAttestedType,
  mapUndefined,
} from "./mappers";
import { dynamicFirstStep } from "./steps/dynamicFirstStep";
import {
  FormSubmissionState,
  LeadFormSubmissionStatus,
  stepOrder,
} from "./types";
import { bypassSuccess } from "./utils";

export interface MultiStepLeadFormFields {
  Website: string;
  Company: string;
  FirstName: string;
  LastName: string;
  Email: string;
  Phone: string;
  Employees_Marketo__c: string;
  Monthly_credit_card_spend__c: string;
  How_Did_You_Hear_About_Us__c: string;
  empower_product_interest__c: string;
  Investor_Category__c: string;
  Is_e_commerce__c: boolean | null;
  Monthly_Sales__c: string | null;
  Lead_Form_Submission_Status__c?: LeadFormSubmissionStatus;
}

export type LeadFormOptions = {
  /**
   * Set to false to bypass validation of required fields for debugging
   */
  requiredFields: boolean;
};

export const defaultFormOptions: LeadFormOptions = {
  requiredFields: true,
} as const;

interface Flags {
  useEcommQuestions: boolean;
}

const getMultiStepLeadFormFields = (
  getValues: UseFormGetValues<FieldValues>,
): MultiStepLeadFormFields => ({
  Website: getValues("website"),
  Company: getValues("companyName"),
  FirstName: getValues("firstName"),
  LastName: getValues("lastName"),
  Email: getValues("workEmail"),
  Phone: getValues("mobileNumber"),
  Employees_Marketo__c: getValues("employeeCount"),
  Monthly_credit_card_spend__c: getValues("monthlyCreditSpend"),
  How_Did_You_Hear_About_Us__c: getValues("howDidYouHearAboutUs"),
  empower_product_interest__c:
    getValues("productsOfInterest")?.join(";") ?? null,
  Investor_Category__c: getValues("fundingSource"),
  Is_e_commerce__c: mapIsEcommToMarketo(getValues("isEcomm")),
  Monthly_Sales__c: mapMonthlySalesToMarketo(getValues("monthlySales")),
});

/**
 * https://help.chilipiper.com/hc/en-us/articles/360053800373
 * Calling ChiliPiper.submit will trigger the following actions:
 *    Collect form data if there is a form in the page (including hidden values) and/or receive key-value data as parameters in the Options object (they will be merged if you’re using both).
 *    Call Chili Piper API endpoint.
 *    Optionally (based on Concierge settings), the Lead/Contact will be created in Salesforce or updated if it exists, with the form values, including hidden value.
 *    The Chili Piper router's logic will be executed (based on Queues) and return the booking calendar link.
 *    The booking calendar is displayed in an iFrame in a lightbox
 *    Once the slot is selected and the meeting is booked, you can choose among three options:
 *      1) provide us with redirection settings, the user will be redirected
 *      2) use the onComplete parameter, onComplete call back will be executed
 *      3) nothing, the user can just close the box.
 */
export const submitLeadRoutingRequest = (
  chiliPiper: NonNullable<WindowTypes["ChiliPiper"]>,
  onSubmitSuccess: (lead?: FormValues) => void,
  trackEvent: AnalyticsApi["trackEvent"],
  lead: MultiStepLeadFormFields | FormValues,
  options?: { account?: string; router?: string },
) => {
  lead.Lead_Form_Submission_Status__c = LeadFormSubmissionStatus.COMPLETE;
  chiliPiper.submit(
    options?.account ?? "brex-com",
    options?.router ?? "brex_lead_form_routing_updated",
    {
      map: true,
      lead,
      // When a lead's submission has been received by Chili Piper and a routing attempt has been made, whether successful or not.
      // This will occur before the onSuccess, onError, or onClose functions are executed to indicate routing has begun.
      onRouted: () => {
        trackEvent(
          new LeadRoutingEvent(
            "Chili Piper",
            LeadRoutingEventType.LEAD_ROUTING_INITIATED,
          ),
        );
      },
      // routed as qualified lead, called after a successful booking
      onSuccess: () => {
        trackEvent(
          new LeadRoutingEvent(
            "Chili Piper",
            LeadRoutingEventType.QUALIFIED_LEAD_AND_A_MEETING_IS_BOOKED,
          ),
        );
        onSubmitSuccess();
      },
      // routed as qualified lead, called after exiting the booking iframe without booking any meetings
      onClose: () => {
        trackEvent(
          new LeadRoutingEvent(
            "Chili Piper",
            LeadRoutingEventType.QUALIFIED_LEAD_AND_A_MEETING_IS_NOT_BOOKED,
          ),
        );
        onSubmitSuccess();
      },
      // catch all for any leads that cannot be routed, usually disqualified lead
      onError: () => {
        trackEvent(
          new LeadRoutingEvent(
            "Chili Piper",
            LeadRoutingEventType.DISQUALIFIED_LEAD,
          ),
        );
        onSubmitSuccess();
      },
    },
  );
};

/**
 *
 * @param customProgramName program name for marketo - will always default to CHILI_PIPER_ECOMM_PROGRAM_TREATMENT value
 * @returns
 */
export const useDynamicLeadFormController = (
  showSuccessScreen: () => void,
  showWaitlistScreen: () => void,
  showSSUTransitionScreen: (lead: MultiStepLeadFormFields) => void,
  createLeadMutation: typeof marketoCreateLeadMutation,
  updateLeadMutation: typeof marketoUpdateLeadMutation,
  { status: leadRoutingScriptStatus }: ReturnType<typeof useLeadRoutingScript>,
  formMeta: FormMetaProps,
) => {
  const {
    customProgramName,
    showCustomLeftContent,
    customFormTitle,
    customSubmitButtonLabel,
    bypassSuccessContentUrl,
    hiddenFields,
  } = formMeta;
  const { trackEvent, identify } = useAnalytics();
  const { navigators } = useSiteContext();
  const prevPath = useAtomValue(referrerAtom);

  const {
    shouldBypass,
    link: bypassLink,
    isInternalLink,
  } = bypassSuccess(bypassSuccessContentUrl);
  const leadForm = useLeadFormFields(trackEvent, defaultFormOptions);
  const { fields, getValues, setValue, country, formState } = leadForm;

  React.useEffect(() => {
    Object.keys(fields).map((field) => {
      const value = localStorage?.getItem(field);
      if (value) {
        /** typecasting to satisfy setting value of only the fields */
        setValue(field as keyof typeof fields, JSON.parse(value), {
          shouldDirty: true,
        });
      }
    });
  }, [fields, setValue]);

  const [state, setState] = React.useState<{
    step: Step;
    prevStep: Step | undefined;
    areButtonsActive: boolean;
    useChilipiper: boolean;
  }>({
    step: dynamicFirstStep,
    prevStep: undefined,
    areButtonsActive: true,
    useChilipiper: true,
  });

  const [flags] = React.useState<Flags>({
    useEcommQuestions: true,
  });

  const [formSubmissionState, setFormSubmissionState] = React.useState(
    FormSubmissionState.NOT_SUBMITTED,
  );

  const { step, areButtonsActive } = state;
  const stepId = step.id;
  const hasPrevStep = step.hasPrevStep(fields);
  const isValid = step.isValid(formState);
  const canDoNext = isValid && areButtonsActive;

  // Keep track of which steps we have seen
  const viewedRef = React.useRef<Record<string, boolean>>({});
  // Keep track of the lead id
  const leadRef = React.useRef({
    id: "",
  });

  React.useEffect(() => {
    if (viewedRef.current[stepId]) return;

    trackEvent(new FormStepViewedEvent(stepId, EMPOWER_MULTI_STEP_LEAD_FORM));

    viewedRef.current[stepId] = true;
  }, [stepId, trackEvent]);

  const setStep = (step: Step) =>
    setState((prev) => ({ ...prev, step, prevStep: prev.step }));
  const setAreButtonsActive = (buttonState: boolean) => {
    setState((prev) => ({ ...prev, areButtonsActive: buttonState }));
  };

  /**
   * Track analytics for submission of final step
   * We need the program name as well as the form id for tracking results.
   * However, the raw program name can result in an eventKey that exceeds the
   * max length and/or contains illegal characters. So we sub in shorter
   * versions.
   */
  const trackSubmission = () => {
    const programName = "SALES";
    const event = new MarketingFormSubmittedEvent(programName, {
      formId: FORM_ID,
    });
    trackEvent(event);
  };

  /**
   * Update lead status to COMPLETED, track analytics, and set form state.
   * Accepts a flag to enable/disable Chilipiper to qualify the lead after
   * form status is SUBMITTED_AND_SUCCEEDED
   *
   */
  const finishWithUpdate = async (useChilipiper: boolean) => {
    setState((prev) => ({ ...prev, useChilipiper }));
    setFormSubmissionState(FormSubmissionState.SUBMISSION_ATTEMPTED);

    const result = await updateLead(
      leadRef.current.id,
      LeadFormStatus.COMPLETED,
      customProgramName,
    );

    trackSubmission();

    if (result.errors) {
      setFormSubmissionState(FormSubmissionState.SUBMITTED_AND_FAILED);
    } else {
      setFormSubmissionState(FormSubmissionState.SUBMITTED_AND_SUCCEEDED);
    }
  };

  /**
   * Side effects after lead form submission succeeded. Possible scenarios:
   *  - FE is still loading or will never be able to load the lead routing script => timeout => `onSubmitSuccess` is called (i.e. users is redirected to the success page), and even if the script is loaded later, it will not be executed.
   *  - FE loads the lead routing script in time => the script will run and `onSubmitSuccess` will be handled by it
   */
  React.useEffect(() => {
    // Only want to proceed when the form is submitted
    if (formSubmissionState !== FormSubmissionState.SUBMITTED_AND_SUCCEEDED) {
      return;
    }

    // Report when the form submission finishes before the script loads
    if (
      (leadRoutingScriptStatus === ScriptLoadingStatus.LOADING ||
        leadRoutingScriptStatus === ScriptLoadingStatus.NOT_STARTED) &&
      state.useChilipiper
    ) {
      return logThirdPartyScriptError(
        "Lead form submitted before ChiliPiper loaded.",
      );
    }

    if (
      leadRoutingScriptStatus === ScriptLoadingStatus.SUCCESSFULLY_LOADED &&
      state.useChilipiper
    ) {
      const lead = getMultiStepLeadFormFields(getValues);
      lead.Website = lead.Email?.split("@")?.at(-1) || "";
      // Leave redirect to the leadRoutingScript since it's now loaded
      return (
        window.ChiliPiper &&
        submitLeadRoutingRequest(
          window.ChiliPiper,
          showSuccessScreen,
          trackEvent,
          lead,
        )
      );
    }

    // Only call submitLeadRoutingRequest once when lead form submission succeeded and leadRoutingScript is loaded in time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formSubmissionState, leadRoutingScriptStatus]);

  const mapCreditCardSpend = mapUndefined(
    (input: CreditSpendOptionValue): QuestionCreditCardSpendConsolidated => {
      switch (input) {
        case "$1M+":
          return QuestionCreditCardSpendConsolidated.MORE_THAN_ONE_MM;
        case "$500K-1M":
          return QuestionCreditCardSpendConsolidated.FIVE_HUNDRED_K_TO_ONE_MM;
        case "$75k - $500K":
          return QuestionCreditCardSpendConsolidated.SEVENTY_FIVE_K_TO_FIVE_HUNDRED_K;
        case "<$75K":
          return QuestionCreditCardSpendConsolidated.UNDER_SEVENTY_FIVE_K;
      }
    },
  );

  const mapEmployeeCount = mapUndefined(
    (input: EmployeeCountOptionValue): QuestionEmployeeCount => {
      switch (input) {
        case "1 - 4":
          return QuestionEmployeeCount.ONE_TO_FOUR;
        case "5 - 20":
          return QuestionEmployeeCount.FIVE_TO_TWENTY;
        case "21 - 50":
          return QuestionEmployeeCount.TWENTY_ONE_TO_FIFTY;
        case "51 - 100":
          return QuestionEmployeeCount.FIFTY_ONE_TO_ONE_HUNDRED;
        case "101 - 500":
          return QuestionEmployeeCount.ONE_HUNDRED_ONE_TO_FIVE_HUNDRED;
        case "501 - 2,500":
          return QuestionEmployeeCount.FIVE_HUNDRED_ONE_TO_TWO_THOUSAND_FIVE_HUNDRED;
        case "2,500+":
          return QuestionEmployeeCount.MORE_THAN_TWO_THOUSAND_FIVE_HUNDRED;
      }
    },
  );

  const mapFundingSource = mapUndefined(
    (input: FundingSourceOptionValue): QuestionFundingSource => {
      switch (input) {
        case "Crowdsourced":
          return QuestionFundingSource.CROWDSOURCE;
        case "Family":
          return QuestionFundingSource.FAMILY;
        case "Initial Coin Offering":
          return QuestionFundingSource.ICO;
        case "Self Funded":
          return QuestionFundingSource.SELF_FUNDED;
        case "professional_investor":
          return QuestionFundingSource.PROFESSIONAL_INVESTMENT;
      }
    },
  );

  const mapProductInterests = mapUndefined(
    (input: ProductInterestOptionValue[]): QuestionIntent[] => {
      return input.map((el) => {
        switch (el) {
          case "Business Account":
            return QuestionIntent.OPEN_BUSINESS_ACCOUNT;
          case "Corporate Card":
            return QuestionIntent.GET_CREDIT_CARD;
          case "Spend Management Software":
            return QuestionIntent.MANAGE_TEAM_EXPENSES;
        }
      });
    },
  );

  const getLeadDetails = (): LeadDetailsInput => {
    const formEmployeeCount = optional(decodeEmployeeCountOptionValue).verify(
      getValues("employeeCount"),
    );

    const formFundingSource = optional(decodeFundingSourceOptionValue).verify(
      getValues("fundingSource"),
    );

    const formProductInterests = optional(
      decodeProductInterestSelection,
    ).verify(getValues("productsOfInterest"));

    const formCreditCardSpend = optional(decodeCreditSpendOptionValue).verify(
      getValues("monthlyCreditSpend"),
    );

    return {
      multiPageVbMmV0: {
        companyName: getValues("companyName"),
        creditCardSpend: mapCreditCardSpend(formCreditCardSpend),
        email: getValues("workEmail"),
        employeeCount: mapEmployeeCount(formEmployeeCount),
        firstName: getValues("firstName"),
        lastName: getValues("lastName"),
        fundingSource: mapFundingSource(formFundingSource),
        phoneNumber:
          formatInternationalPhoneNumber({
            country,
            value: getValues("mobileNumber"),
          }) ?? null,
        productInterests: mapProductInterests(formProductInterests),
        selfAttestedSource: getValues("howDidYouHearAboutUs"),
        website: getValues("website"),
        leadSelfAttestedType: mapSelfAttestedType(getValues("isEcomm")),
        salesVolumeLeadForm: mapSalesVolume(getValues("monthlySales")),
      },
    };
  };

  /**
   * Create a new lead
   * @param customProgramName custom program name for marketo if not using the sales lead form
   * @returns leadId
   */
  const createLead = async (customProgramName: string) => {
    const input: CreateLeadInput = {
      clientMutationId: uuidv4(),
      idempotencyKey: uuidv4(),
      marketoProgramName: customProgramName,
      attributionMetadata: {
        fbc: getFBC(),
        fbp: getFBP(),
        gclid: getGCLID(),
        liFatId: getLiFatId(),
        fullUrl: window.location.href,
        referralCode: getRefCode() ?? getUserRefCode() ?? null,
        segmentAnonymousId: getAnonymousId(),
        ...hiddenFields,
      },
      leadFormType: LeadFormType.MULTI_PAGE_VB_MM_V0,
      leadFormStatus: LeadFormStatus.PENDING,
      leadDetails: getLeadDetails(),
    };

    return await createLeadMutation(input);
  };

  /**
   * @param id The id of the lead to update
   * @param leadFormStatus The status to update the lead to
   * @param customProgramName Custom program name if not regular sales lead form
   */
  const updateLead = async (
    id: string,
    leadFormStatus: UpdateLeadInput["leadFormStatus"],
    customProgramName: string,
  ) => {
    const input: UpdateLeadInput = {
      clientMutationId: uuidv4(),
      idempotencyKey: uuidv4(),
      id,
      leadFormType: LeadFormType.MULTI_PAGE_VB_MM_V0,
      leadFormStatus,
      leadDetails: getLeadDetails(),
      marketoProgramName: customProgramName,
    };

    return await updateLeadMutation(input);
  };

  const next = async () => {
    // Button has already been pressed and its still processing the previous click
    if (!state.areButtonsActive) return;

    // Make sure we don't handle other clicks while we are handling this click
    setAreButtonsActive(false);

    // We don't permit going forward while there are validation errors
    if (!isValid) {
      // Reenable buttons since we arent going to navigate + animate
      return setAreButtonsActive(true);
    }

    // Update lead if we know the id, otherwise create
    const createOrUpdateLead = async () => {
      setFormSubmissionState(FormSubmissionState.SUBMISSION_ATTEMPTED);
      if (leadRef.current.id)
        await updateLead(
          leadRef.current.id,
          LeadFormStatus.PENDING,
          customProgramName,
        );
      else {
        const { id, leadToken } = await createLead(customProgramName);
        leadRef.current.id = id;
        /**
         * Store result in cookie
         */
        updateCookie("leadToken", leadToken);

        const cookieCategories = getCookieCategories();

        /**
         * identify with all of user info
         */
        identify({
          properties: {
            email: getValues("workEmail"),
            formFillUrl: window.location.href,
            partnerId: getRefCode() ?? getUserRefCode() ?? null,
            formCta: customSubmitButtonLabel,
            referrer: prevPath || document.referrer,
          },
          // Marketo V2: false if cookieCategories is undefined
          integrations: { "Marketo V2": cookieCategories.performance },
        });
      }
      // TODO this state should probably be renamed to something like "idle"
      setFormSubmissionState(FormSubmissionState.NOT_SUBMITTED);
    };

    /**
     * Function that handles rerouting, contains some logic based on type of url (external / internal)
     * Moved out to a function for reusability
     */
    const rerouteIfBypass = () => {
      isInternalLink
        ? navigators.function(bypassLink)
        : (window.location.href = bypassLink);
    };

    await step.next({
      createOrUpdateLead,
      setStep,
      fields,
      formState,
      finishWithUpdate: async () => {
        await finishWithUpdate(false);
        if (shouldBypass) {
          rerouteIfBypass();
        } else {
          showSuccessScreen();
        }
      },
      finishWithSSU: async () => {
        await finishWithUpdate(false);
        showSSUTransitionScreen(getMultiStepLeadFormFields(getValues));
      },
      finishWithWaitList: async () => {
        await finishWithUpdate(false);
        if (shouldBypass) {
          rerouteIfBypass();
        } else {
          showWaitlistScreen();
        }
      },
    });
  };

  const onError = (e: unknown) => {
    if (e instanceof Error) logMiscError(e.message);
    if (typeof e === "string") logMiscError(e);
    setFormSubmissionState(FormSubmissionState.SUBMITTED_AND_FAILED);
    setAreButtonsActive(true);
  };

  return {
    state,
    formSubmissionState,
    setFormSubmissionState,
    currentState: stepId,
    currentStepIndex: step,
    totalStepCount: stepOrder.length,
    setAreButtonsActive,
    hasPrevStep: hasPrevStep,
    canDoNext,
    canDoBack: hasPrevStep && state.areButtonsActive,
    next: withErrorHandling(onError, next),
    back: () => {
      // Button has already been pressed and its still processing the previous click
      if (!state.areButtonsActive) return;
      if (!hasPrevStep) return;
      // Make sure we don't handle other clicks while we are handling this click
      setAreButtonsActive(false);

      step.back(setStep);
    },
    fields,
    formState,
    flags,
    showCustomLeftContent,
    customFormTitle,
    customSubmitButtonLabel,
    bypassSuccessContentUrl,
  };
};

export type DynamicLeadFormController = ReturnType<
  typeof useDynamicLeadFormController
>;

export interface FieldValues {
  workEmail: string;
  firstName: string;
  lastName: string;
  mobileNumber: string;
  website: string;
  companyName: string;
  productsOfInterest: string[];
  employeeCount: string;
  monthlyCreditSpend: string;
  monthlySales: string;
  isEcomm: string;
  fundingSource: string;
  howDidYouHearAboutUs: string;
}
